import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { buildTheme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';

import LoginPage           from './pages/LoginPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import DashboardLayout     from './components/DashboardLayout';
import EventsPage          from './pages/events';
import EventEditor         from './pages/events/EventEditor';
import LobbyAdminView      from './pages/LobbyAdminView';
import FormsPage           from './pages/FormsPage';
import ParticipantsPage    from './pages/ParticipantsPage';
import SubmissionsPage     from './pages/SubmissionsPage';
import CollaboratorsPage   from './pages/CollaboratorsPage';

const theme = buildTheme();

const Loader = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' }}>
    <CircularProgress sx={{ color: '#1075BA' }} />
  </Box>
);

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, isSuperAdmin, isLobbyAdmin } = useAuth();
  if (loading) return <Loader />;
  if (isAuthenticated) {
    if (isSuperAdmin)   return <Navigate to="/superadmin" replace />;
    if (isLobbyAdmin)   return <Navigate to="/lobby-admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loader />;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();
  if (loading) return <Loader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

const LobbyAdminRoute = ({ children }) => {
  const { isAuthenticated, isLobbyAdmin, loading } = useAuth();
  if (loading) return <Loader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isLobbyAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

const PermissionRoute = ({ permission, children }) => {
  const { isAuthenticated, loading, hasPermission, isLobbyAdmin } = useAuth();
  if (loading) return <Loader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isLobbyAdmin) return <Navigate to="/lobby-admin" replace />;
  if (!hasPermission(permission)) return <Navigate to="/dashboard/events" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login"      element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />

      {/* ── Lobby Admin — fullscreen, no dashboard chrome ── */}
      <Route path="/lobby-admin" element={<LobbyAdminRoute><LobbyAdminView /></LobbyAdminRoute>} />

      {/* ── Full-screen editors (no sidebar, no layout wrapper) ── */}
      {/* Legacy redirect — bare /editor/events/:id goes to details */}
      <Route path="/editor/events/:id" element={<ProtectedRoute><Navigate to="details" replace /></ProtectedRoute>} />

      {/* Single splat route — EventEditor parses tab + optional subtab from the wildcard */}
      <Route path="/editor/:id/*" element={<ProtectedRoute><EventEditor /></ProtectedRoute>} />

      {/* ── Main dashboard (with sidebar) ── */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index                element={<Navigate to="/dashboard/events" replace />} />
        <Route path="events"        element={<PermissionRoute permission="events:view"><EventsPage /></PermissionRoute>} />
        <Route path="forms"         element={<PermissionRoute permission="forms:view"><FormsPage /></PermissionRoute>} />
        <Route path="forms/:id"     element={<PermissionRoute permission="forms:view"><FormsPage /></PermissionRoute>} />
        <Route path="participants"  element={<PermissionRoute permission="participants:view"><ParticipantsPage /></PermissionRoute>} />
        <Route path="submissions"   element={<PermissionRoute permission="submissions:view"><SubmissionsPage /></PermissionRoute>} />
        <Route path="collaborators" element={<PermissionRoute permission="users:view"><CollaboratorsPage /></PermissionRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  )
}